import { api } from '../utils/api'
import type {
  Product,
  ProductInput,
  Ingredient,
  ProductAdditionsBlock,
  ProductExceptionsBlock,
  AdditionOption,
  ExceptionOption,
} from '../types/product'

interface ProductApi {
  id: number
  name: string
  description: string
  price: number
  category: string | { id: number; name: string }
  image_path?: string | null
  active: boolean
  ingredients?: string[]
  maxFlavors?: number
  additions?: ProductAdditionsBlock
  exceptions?: ProductExceptionsBlock
  createdAt?: string
  updatedAt?: string
}

function mapIngredient(name: string, index: number): Ingredient {
  return {
    id: index,
    name,
  }
}

function cloneAddition(option: AdditionOption): AdditionOption {
  return { ...option, priceDelta: Number(option.priceDelta ?? 0), maxQty: Number(option.maxQty ?? 1) }
}

function cloneException(option: ExceptionOption): ExceptionOption {
  return {
    ...option,
    affectsPrice: Boolean(option.affectsPrice),
    priceDelta: option.affectsPrice ? Number(option.priceDelta ?? 0) : 0,
  }
}

function normalizeAdditions(additions?: any): ProductAdditionsBlock {
  if (!additions) {
    return { enabled: false, options: [] }
  }

  // If already in block format
  if (additions.enabled !== undefined) {
    return {
      enabled: Boolean(additions.enabled),
      options: (additions.options ?? []).map(cloneAddition),
    }
  }

  // If it's a flat array from backend relationship
  if (Array.isArray(additions)) {
    return {
      enabled: additions.length > 0,
      options: additions.map((a: any) => ({
        key: a.key,
        name: a.name,
        priceDelta: Number(a.price_delta ?? 0),
        maxQty: Number(a.max_qty ?? 1),
        active: Boolean(a.active ?? true),
      })),
    }
  }

  return { enabled: false, options: [] }
}

function normalizeExceptions(exceptions?: any): ProductExceptionsBlock {
  if (!exceptions) {
    return { enabled: false, options: [] }
  }

  // If already in block format
  if (exceptions.enabled !== undefined) {
    return {
      enabled: Boolean(exceptions.enabled),
      options: (exceptions.options ?? []).map(cloneException),
    }
  }

  // If it's a flat array from backend relationship
  if (Array.isArray(exceptions)) {
    return {
      enabled: exceptions.length > 0,
      options: exceptions.map((e: any) => ({
        key: e.key,
        name: e.name,
        affectsPrice: Boolean(e.affects_price ?? false),
        priceDelta: Number(e.price_delta ?? 0),
        active: Boolean(e.active ?? true),
      })),
    }
  }

  return { enabled: false, options: [] }
}

function mapProduct(payload: any): Product {
  const { id, category, ingredients = [], image_path, imageUrl, imagePath, additions, exceptions, maxFlavors, ...rest } = payload

  const resolvedCategory =
    typeof category === 'string'
      ? { id: Number(category) || 0, name: category }
      : category
        ? { id: category.id, name: category.name }
        : null

  return {
    id: id,
    imageUrl: imageUrl || image_path || imagePath || null,
    category: resolvedCategory,
    ingredients: (ingredients ?? []).map(mapIngredient),
    maxFlavors: typeof maxFlavors === 'number' ? maxFlavors : undefined,
    additions: normalizeAdditions(additions),
    exceptions: normalizeExceptions(exceptions),
    ...rest,
  }
}

export async function listProducts(params?: { q?: string }): Promise<Product[]> {
  const { data } = await api.get<ProductApi[]>('/products', {
    params: params?.q ? { q: params.q } : undefined,
  })
  return data.map(mapProduct)
}

export async function getProduct(id: string | number): Promise<Product> {
  const { data } = await api.get<ProductApi>(`/products/${id}`)
  return mapProduct(data)
}

export async function createProduct(fd: FormData): Promise<Product> {
  if (!fd.has('maxFlavors')) {
    fd.append('maxFlavors', '0')
  }
  if (!fd.has('additions')) {
    fd.append('additions', JSON.stringify({ enabled: false, options: [] }))
  }
  if (!fd.has('exceptions')) {
    fd.append('exceptions', JSON.stringify({ enabled: false, options: [] }))
  }
  const { data } = await api.post<ProductApi>('/products', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return mapProduct(data)
}

export async function updateProduct(
  id: string | number,
  body: Partial<ProductInput> | FormData,
): Promise<Product> {
  if (body instanceof FormData) {
    if (!body.has('_method')) {
      body.append('_method', 'PUT')
    }
    const { data } = await api.post<ProductApi>(`/products/${id}`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return mapProduct(data)
  }

  const payload: Partial<ProductInput> = {
    ...body,
    additions: body.additions ?? { enabled: false, options: [] },
    exceptions: body.exceptions ?? { enabled: false, options: [] },
    maxFlavors: typeof body.maxFlavors === 'number' ? body.maxFlavors : body.maxFlavors ? Number(body.maxFlavors) : 0,
  }
  const { data } = await api.put<ProductApi>(`/products/${id}`, payload)
  return mapProduct(data)
}

export async function deleteProduct(id: string | number): Promise<void> {
  await api.delete(`/products/${id}`)
}
